<template>
  <v-container fluid>
    <v-row align="center">
      <v-col cols="12" md="6">
        <v-btn color="info" @click="addNew"><v-icon left small>fa-plus</v-icon> Nowy szablon</v-btn>
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-switch v-model="includeRetired" label="Pokaż wycofane" dense hide-details class="d-inline-block" @change="load"></v-switch>
      </v-col>
    </v-row>

    <v-data-table :items="items" :headers="headers" :loading="loading" hide-default-footer disable-pagination
      no-data-text="Brak szablonów - dodaj pierwszy i wgraj plik Word">
      <template v-slot:item.title="{ item }">
        <div>{{ item.title }}</div>
        <div class="grey--text caption">{{ item.code }} v{{ item.version }} · {{ labels.legalForm[item.legalFormRequirement] }} · {{ labels.captureMethod[item.captureMethod] }} · {{ labels.context[item.collectionContext] }}</div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" dark>{{ labels.templateStatus[item.status] }}</v-chip>
        <v-chip small outlined class="ml-1" v-if="!item.hasDocx" color="orange">brak pliku Word</v-chip>
        <v-chip small outlined class="ml-1" v-if="item.requiresDualConsent">zgoda podwójna</v-chip>
      </template>
      <template v-slot:item.approvedAt="{ item }">{{ formatDate(item.approvedAt) }}</template>
      <template v-slot:item.actions="{ item }">
        <template v-if="item.status === 'DRAFT'">
          <v-icon small class="mr-2" @click="edit(item)" title="Edytuj szkic">fa-edit</v-icon>
          <v-icon small class="mr-2" @click="openUpload(item)" title="Wgraj plik Word">fa-file-word</v-icon>
          <v-icon small class="mr-2" @click="preview(item)" title="Podgląd tekstu dla tabletu" v-if="item.hasDocx">fa-eye</v-icon>
          <v-icon small class="mr-2" @click="downloadDocx(item)" title="Pobierz plik Word" v-if="item.hasDocx">fa-download</v-icon>
          <v-icon small class="mr-2" @click="publish(item)" title="Opublikuj" color="green" v-if="item.hasDocx">fa-check-circle</v-icon>
          <v-icon small class="mr-2" @click="remove(item)" title="Usuń szkic">fa-trash-alt</v-icon>
        </template>
        <template v-else>
          <v-icon small class="mr-2" @click="preview(item)" title="Podgląd">fa-eye</v-icon>
          <v-icon small class="mr-2" @click="downloadDocx(item)" title="Pobierz plik Word" v-if="item.hasDocx">fa-download</v-icon>
          <v-icon small class="mr-2" @click="newVersion(item)" title="Nowa wersja (szkic)">fa-code-branch</v-icon>
          <v-icon small class="mr-2" @click="retire(item)" title="Wycofaj" v-if="item.status === 'PUBLISHED'">fa-ban</v-icon>
        </template>
      </template>
    </v-data-table>

    <AddEditSigningTemplate v-if="showEdit" :visible="showEdit" :id="selectedId" @close="showEdit = false" @saved="onSaved"></AddEditSigningTemplate>

    <v-dialog v-model="showUpload" max-width="520px" persistent>
      <v-card>
        <v-toolbar dark class="primary" dense><v-toolbar-title>Plik Word szablonu</v-toolbar-title></v-toolbar>
        <v-card-text class="pt-4">
          <p class="grey--text">Dokument .docx z placeholderami {{ ph('nazwaPola') }}. Tabele i obrazy nie są dozwolone (tablet ich nie pokaże).</p>
          <v-file-input v-model="file" label="Szablon .docx" accept=".docx" prepend-icon="fa-paperclip" outlined dense></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showUpload = false">Anuluj</v-btn>
          <v-btn color="primary" :disabled="!file" :loading="uploading" @click="upload">Wgraj</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPreview" max-width="820px" scrollable>
      <v-card v-if="previewed">
        <v-toolbar dark class="primary" dense>
          <v-btn icon @click="showPreview = false" dark><v-icon>fa-times</v-icon></v-btn>
          <v-toolbar-title>{{ previewed.code }} v{{ previewed.version }} · {{ previewed.title }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="warning" dense text v-for="(w, i) in previewed.warnings" :key="'w' + i">{{ w }}</v-alert>
          <v-alert type="error" dense text v-for="(p, i) in problems" :key="'p' + i">{{ p }}</v-alert>
          <v-alert type="success" dense text v-if="checked && problems.length === 0">Szablon gotowy do publikacji.</v-alert>
          <div class="caption grey--text" v-if="previewed.contentSha256">Hash treści: {{ previewed.contentSha256 }} · plik: {{ previewed.docxFileName }}</div>
          <pre class="mt-3 pa-3 grey lighten-4" style="white-space: pre-wrap; font-family: inherit">{{ previewed.contentText }}</pre>
          <div class="subtitle-2 mt-3">Pola</div>
          <div v-for="f in previewed.fields" :key="f.name" class="caption">
            <b>{{ f.name }}</b> - {{ f.label }} ({{ f.type }}{{ f.required ? ', wymagane' : '' }}{{ f.options ? ': ' + f.options.join(' / ') : '' }})
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// Administracja szablonów dokumentów (decyzja D1: źródłem jest plik Word).
import { podpisyService, podpisyLabels, abpError } from "@/services/podpisy.service";
import AddEditSigningTemplate from "./AddEditSigningTemplate";
import { mapActions } from "vuex";
import swal from "sweetalert";
import dayjs from "dayjs";

export default {
  name: "SigningTemplates",
  components: { AddEditSigningTemplate },
  data() {
    return {
      loading: false,
      uploading: false,
      items: [],
      includeRetired: false,
      showEdit: false,
      selectedId: null,
      showUpload: false,
      uploadTarget: null,
      file: null,
      showPreview: false,
      previewed: null,
      problems: [],
      checked: false,
      labels: podpisyLabels,
      headers: [
        { text: "Dokument", value: "title", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Zatwierdzono", value: "approvedAt", sortable: false },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],
    };
  },
  methods: {
    ...mapActions("alert", ["error", "success"]),
    ...mapActions("title", ["updateTitle"]),
    formatDate(v) { return v ? dayjs(v).format("DD-MM-YYYY") : ""; },
    ph(name) { return "{{" + name + "}}"; },
    statusColor(s) { return { DRAFT: "grey", PUBLISHED: "green", RETIRED: "blue-grey" }[s] || "grey"; },
    load() {
      this.loading = true;
      podpisyService.getTemplates(this.includeRetired).then(resp => { this.items = resp.data.result; })
        .catch(e => this.error(abpError(e, "Nie można pobrać szablonów")))
        .finally(() => { this.loading = false; });
    },
    addNew() { this.selectedId = null; this.showEdit = true; },
    edit(item) { this.selectedId = item.id; this.showEdit = true; },
    onSaved() { this.showEdit = false; this.load(); },
    openUpload(item) { this.uploadTarget = item; this.file = null; this.showUpload = true; },
    upload() {
      this.uploading = true;
      podpisyService.uploadTemplateDocx(this.uploadTarget.id, this.file).then(resp => {
        this.showUpload = false;
        this.success("Plik wgrany");
        this.previewed = resp.data.result;
        this.problems = [];
        this.checked = false;
        this.showPreview = true;
        this.load();
      }).catch(e => this.error(abpError(e, "Nie można wgrać pliku")))
        .finally(() => { this.uploading = false; });
    },
    preview(item) {
      Promise.all([podpisyService.getTemplate(item.id), item.status === "DRAFT" ? podpisyService.checkTemplate(item.id) : Promise.resolve(null)])
        .then(([t, c]) => {
          this.previewed = t.data.result;
          this.problems = c ? c.data.result : [];
          this.checked = !!c;
          this.showPreview = true;
        }).catch(e => this.error(abpError(e, "Nie można pobrać szablonu")));
    },
    downloadDocx(item) {
      podpisyService.getTemplateDocx(item.id).then(resp => {
        const r = resp.data.result;
        const bin = atob(r.contentBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = r.fileName || (item.code + "-v" + item.version + ".docx");
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }).catch(e => this.error(abpError(e, "Nie można pobrać pliku Word")));
    },
    publish(item) {
      swal({ title: "Opublikować szablon?", text: item.code + " v" + item.version + "\nPo publikacji treść jest niezmienna; poprzednia wersja o tym kodzie zostanie wycofana.",
        icon: "warning", buttons: ["Nie", "Opublikuj"] }).then(ok => {
        if (!ok) return;
        podpisyService.publishTemplate(item.id).then(() => { this.success("Szablon opublikowany"); this.load(); })
          .catch(e => swal({ title: "Nie można opublikować", text: abpError(e, "Błąd publikacji"), icon: "error", button: "OK" }));
      });
    },
    retire(item) {
      swal({ title: "Wycofać szablon?", text: "Nie będzie można tworzyć nowych sesji z tej wersji. Podpisane dokumenty pozostają.", icon: "warning", buttons: ["Nie", "Wycofaj"], dangerMode: true })
        .then(ok => { if (ok) podpisyService.retireTemplate(item.id).then(() => this.load()).catch(e => this.error(abpError(e, "Błąd"))); });
    },
    remove(item) {
      swal({ title: "Usunąć szkic?", icon: "warning", buttons: ["Nie", "Usuń"], dangerMode: true })
        .then(ok => { if (ok) podpisyService.deleteTemplateDraft(item.id).then(() => this.load()).catch(e => this.error(abpError(e, "Błąd"))); });
    },
    newVersion(item) {
      swal({ title: "Nowa wersja " + item.code, text: "Podaj numer nowej wersji (obecna: " + item.version + ")", content: "input", buttons: ["Anuluj", "Utwórz szkic"] })
        .then(version => {
          if (!version) return;
          podpisyService.createNextTemplateVersion(item.id, version).then(resp => {
            this.success("Utworzono szkic v" + version);
            this.load();
            this.edit(resp.data.result);
          }).catch(e => this.error(abpError(e, "Nie można utworzyć wersji")));
        });
    },
  },
  created() {
    this.updateTitle("Szablony dokumentów do podpisu");
  },
  mounted() {
    this.load();
  },
};
</script>
